import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Download, Eye, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { useLanguage } from "@/hooks/use-language";
import { useState } from "react";
import { InvoiceViewDialog } from "@/components/invoices/InvoiceViewDialog";
import { PaymentMethodDialog } from "./PaymentMethodDialog";
import DOMPurify from "dompurify";

export function ClientInvoices() {
  const { language } = useLanguage();
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  
  const t = (key: string) => {
    const translations: Record<string, Record<string, string>> = {
      en: {
        title: "My Invoices",
        subtitle: "View and download your invoices",
        noInvoices: "No invoices yet",
        noInvoicesMsg: "Your invoices will appear here once we send them",
        issued: "Issued",
        due: "Due",
        paymentInstructions: "Payment Instructions:",
        view: "View",
        download: "Download",
        paid: "paid",
        sent: "sent",
        overdue: "overdue",
        viewSuccess: "Opening invoice...",
        downloadSuccess: "Downloading invoice...",
      },
      fr: {
        title: "Mes Factures",
        subtitle: "Consultez et téléchargez vos factures",
        noInvoices: "Aucune facture",
        noInvoicesMsg: "Vos factures apparaîtront ici une fois envoyées",
        issued: "Émise",
        due: "Échéance",
        paymentInstructions: "Instructions de paiement:",
        view: "Voir",
        download: "Télécharger",
        paid: "payée",
        sent: "envoyée",
        overdue: "en retard",
        viewSuccess: "Ouverture de la facture...",
        downloadSuccess: "Téléchargement de la facture...",
      },
      ar: {
        title: "فواتيري",
        subtitle: "عرض وتحميل فواتيرك",
        noInvoices: "لا توجد فواتير بعد",
        noInvoicesMsg: "ستظهر فواتيرك هنا بمجرد إرسالها",
        issued: "تاريخ الإصدار",
        due: "تاريخ الاستحقاق",
        paymentInstructions: "تعليمات الدفع:",
        view: "عرض",
        download: "تحميل",
        paid: "مدفوعة",
        sent: "مرسلة",
        overdue: "متأخرة",
        viewSuccess: "جاري فتح الفاتورة...",
        downloadSuccess: "جاري تحميل الفاتورة...",
      },
      it: {
        title: "Le Mie Fatture",
        subtitle: "Visualizza e scarica le tue fatture",
        noInvoices: "Nessuna fattura",
        noInvoicesMsg: "Le tue fatture appariranno qui una volta inviate",
        issued: "Emessa",
        due: "Scadenza",
        paymentInstructions: "Istruzioni di pagamento:",
        view: "Visualizza",
        download: "Scarica",
        paid: "pagata",
        sent: "inviata",
        overdue: "scaduta",
        viewSuccess: "Apertura fattura...",
        downloadSuccess: "Download fattura...",
      },
    };
    return translations[language]?.[key] || translations.en[key];
  };
  
  const { data: invoices, isLoading } = useQuery({
    queryKey: ["client-invoices"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("invoices")
        .select(`
          *,
          invoice_items (*)
        `)
        .eq("client_email", user.email)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  };

  const handleDownloadInvoice = (invoice: any) => {
    // Create invoice HTML for direct download
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice.invoice_number}</title>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 40px; background: white; color: black; }
            .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { color: #667eea; font-size: 32px; margin-bottom: 10px; }
            .grid { display: flex; gap: 40px; margin-bottom: 40px; }
            .grid > div { flex: 1; }
            .section-title { font-size: 16px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 10px; }
            .details { font-size: 14px; line-height: 1.8; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .text-right { text-align: right; }
            .totals { margin-top: 30px; display: flex; justify-content: flex-end; }
            .totals-box { width: 300px; }
            .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #ddd; }
            .totals-total { display: flex; justify-content: space-between; padding: 15px 0; border-top: 2px solid #333; margin-top: 10px; font-size: 20px; font-weight: bold; color: #667eea; }
            .footer { text-align: center; margin-top: 50px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>INVOICE</h1>
            <p>Spotlight ADZ - AI-Powered Marketing Agency</p>
          </div>
          <div class="grid">
            <div>
              <h3 class="section-title">Invoice Details</h3>
              <div class="details">
                <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
                <p><strong>Issue Date:</strong> ${format(new Date(invoice.issue_date), "MMMM dd, yyyy")}</p>
                <p><strong>Due Date:</strong> ${format(new Date(invoice.due_date), "MMMM dd, yyyy")}</p>
                <p><strong>Status:</strong> ${invoice.status}</p>
              </div>
            </div>
            <div>
              <h3 class="section-title">Bill To</h3>
              <div class="details">
                <p><strong>${invoice.client_name}</strong></p>
                <p>${invoice.client_email}</p>
                ${invoice.client_address ? `<p>${invoice.client_address}</p>` : ''}
              </div>
            </div>
          </div>
          <h3 class="section-title">Items</h3>
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Unit Price</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.invoice_items?.map((item: any) => `
                <tr>
                  <td>${item.description}</td>
                  <td class="text-right">${item.quantity}</td>
                  <td class="text-right">${invoice.currency} ${Number(item.unit_price).toFixed(2)}</td>
                  <td class="text-right">${invoice.currency} ${Number(item.amount).toFixed(2)}</td>
                </tr>
              `).join('') || ''}
            </tbody>
          </table>
          <div class="totals">
            <div class="totals-box">
              <div class="totals-row">
                <span>Subtotal:</span>
                <span>${invoice.currency} ${Number(invoice.subtotal).toFixed(2)}</span>
              </div>
              ${invoice.tax_rate > 0 ? `
                <div class="totals-row">
                  <span>Tax (${invoice.tax_rate}%):</span>
                  <span>${invoice.currency} ${Number(invoice.tax_amount).toFixed(2)}</span>
                </div>
              ` : ''}
              ${invoice.discount_amount > 0 ? `
                <div class="totals-row" style="color: green;">
                  <span>Discount:</span>
                  <span>-${invoice.currency} ${Number(invoice.discount_amount).toFixed(2)}</span>
                </div>
              ` : ''}
              <div class="totals-total">
                <span>Total:</span>
                <span>${invoice.currency} ${Number(invoice.total_amount).toFixed(2)}</span>
              </div>
            </div>
          </div>
          ${invoice.payment_instructions ? `
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              <h3 class="section-title">Payment Instructions</h3>
              <p class="details">${invoice.payment_instructions}</p>
            </div>
          ` : ''}
          ${invoice.terms || invoice.notes ? `
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd;">
              ${invoice.terms ? `
                <div style="margin-bottom: 20px;">
                  <h3 style="font-weight: bold; margin-bottom: 10px;">Terms & Conditions</h3>
                  <p class="details">${invoice.terms}</p>
                </div>
              ` : ''}
              ${invoice.notes ? `
                <div>
                  <h3 style="font-weight: bold; margin-bottom: 10px;">Notes</h3>
                  <p class="details">${invoice.notes}</p>
                </div>
              ` : ''}
            </div>
          ` : ''}
          <div class="footer">
            <p>Thank you for your business!</p>
            <p>© ${new Date().getFullYear()} Spotlight ADZ. All rights reserved.</p>
          </div>
        </body>
      </html>
    `;

    // Open print dialog for PDF save
    const printWindow = window.open('', '', 'height=800,width=800');
    if (printWindow) {
      printWindow.document.write(DOMPurify.sanitize(invoiceHTML));
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "sent":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "overdue":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
      </div>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Card className="p-12">
        <div className="text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">{t("noInvoices")}</p>
          <p className="text-sm text-muted-foreground mt-2">
            {t("noInvoicesMsg")}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <InvoiceViewDialog 
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        invoice={selectedInvoice}
      />
      {selectedInvoice && (
        <PaymentMethodDialog
          invoice={selectedInvoice}
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
        />
      )}
      <div className="space-y-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{t("title")}</h2>
        <p className="text-muted-foreground mt-1">{t("subtitle")}</p>
      </div>

      <div className="grid gap-4">
        {invoices.map((invoice) => (
          <Card key={invoice.id} className="p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">{invoice.invoice_number}</h3>
                  <Badge className={getStatusColor(invoice.status)}>
                    {t(invoice.status)}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>{t("issued")}: {format(new Date(invoice.issue_date), "MMM dd, yyyy")}</p>
                  <p>{t("due")}: {format(new Date(invoice.due_date), "MMM dd, yyyy")}</p>
                  {invoice.notes && <p className="mt-2">{invoice.notes}</p>}
                </div>
              </div>
              <div className="text-left md:text-right">
                <p className="text-2xl font-bold mb-4">
                  {invoice.currency} {invoice.total_amount.toFixed(2)}
                </p>
                <div className="flex md:justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleViewInvoice(invoice)}>
                    <Eye className="h-4 w-4 mr-1" />
                    {t("view")}
                  </Button>
                  <Button variant="default" size="sm" onClick={() => handleDownloadInvoice(invoice)}>
                    <Download className="h-4 w-4 mr-1" />
                    {t("download")}
                  </Button>
                  {invoice.status !== 'paid' && (
                    <Button size="sm" onClick={() => {
                      setSelectedInvoice(invoice);
                      setPaymentDialogOpen(true);
                    }}>
                      <CreditCard className="h-4 w-4 mr-1" />
                      Pay Now
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {invoice.payment_instructions && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium mb-1">{t("paymentInstructions")}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {invoice.payment_instructions}
                </p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
    </>
  );
}
